import { decimalScale, decimalToUnits, sumDecimal, unitsToDecimal } from '../../components/data-grid/money';

/**
 * `GET /journal` returns entries with their lines (AD-3). The screen folds one
 * entry into one row, but every sum is done on decimal strings at integer
 * scale — a running balance is never a JavaScript number.
 */

export type JournalSide = 'D' | 'C';

export interface JournalLine {
  id: string;
  side: JournalSide;
  accountCode: string;
  amount: string;
  currency: string;
  amountCur: string;
  quantity: string | null;
  dims: Record<string, string>;
  description: string | null;
}

export interface JournalEntry {
  id: string;
  date: string;
  periodId: string;
  document: { id: string; type: string };
  rule: { id: string; version: number };
  isReversal: boolean;
  memo: string | null;
  lines: JournalLine[];
}

export interface JournalFilters {
  accountPrefix: string;
  periodId: string;
  from: string;
  to: string;
  documentType: string;
  side: '' | JournalSide;
  dims: Record<string, string>;
}

export const EMPTY_JOURNAL_FILTERS: JournalFilters = {
  accountPrefix: '',
  periodId: '',
  from: '',
  to: '',
  documentType: '',
  side: '',
  dims: {},
};

export interface JournalRow {
  key: string;
  entryId: string;
  date: string;
  debitAccounts: string;
  creditAccounts: string;
  amount: string;
  dimensions: string;
  documentId: string;
  documentType: string;
  isReversal: boolean;
  memo: string | null;
}

export interface JournalTotals {
  debit: string;
  credit: string;
  difference: string;
  balanced: boolean;
}

export function formatDims(dims: Record<string, string>): string {
  return Object.entries(dims)
    .filter(([, value]) => value !== '')
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([slot, value]) => `${slot}: ${value}`)
    .join(' · ');
}

export function toJournalRows(entries: readonly JournalEntry[]): JournalRow[] {
  return entries.map((entry) => {
    const debit = entry.lines.filter((line) => line.side === 'D');
    const credit = entry.lines.filter((line) => line.side === 'C');
    const dims = debit.flatMap((line) => Object.entries(line.dims));
    const dimensions: Record<string, string> = {};
    for (const [slot, value] of dims) {
      dimensions[slot] = value;
    }
    return {
      key: entry.id,
      entryId: entry.id,
      date: entry.date,
      debitAccounts: debit.map((line) => line.accountCode).join(', '),
      creditAccounts: credit.map((line) => line.accountCode).join(', '),
      amount: sumDecimal(debit.map((line) => line.amount)),
      dimensions: formatDims(dimensions),
      documentId: entry.document.id,
      documentType: entry.document.type,
      isReversal: entry.isReversal,
      memo: entry.memo,
    };
  });
}

export function journalTotals(entries: readonly JournalEntry[]): JournalTotals {
  const debit: string[] = [];
  const credit: string[] = [];
  for (const entry of entries) {
    for (const line of entry.lines) {
      if (line.side === 'D') {
        debit.push(line.amount);
      } else {
        credit.push(line.amount);
      }
    }
  }
  const debitTotal = sumDecimal(debit);
  const creditTotal = sumDecimal(credit);
  const scale = Math.max(decimalScale(debitTotal), decimalScale(creditTotal), 4);
  const difference = unitsToDecimal(
    decimalToUnits(debitTotal, scale) - decimalToUnits(creditTotal, scale),
    scale,
  );
  return { debit: debitTotal, credit: creditTotal, difference, balanced: difference === unitsToDecimal(0n, scale) };
}

/** Cumulative D − C after each entry, in order, as decimal strings. */
export function runningBalance(entries: readonly JournalEntry[]): string[] {
  const scale = entries.reduce(
    (widest, entry) => entry.lines.reduce((inner, line) => Math.max(inner, decimalScale(line.amount)), widest),
    4,
  );
  const balances: string[] = [];
  let running = 0n;
  for (const entry of entries) {
    for (const line of entry.lines) {
      running += (line.side === 'D' ? 1n : -1n) * decimalToUnits(line.amount, scale);
    }
    balances.push(unitsToDecimal(running, scale));
  }
  return balances;
}

/** The query string the screen sends; an empty filter does not narrow. */
export function buildJournalQuery(filters: JournalFilters): string {
  const params = new URLSearchParams();
  const singles: Array<[string, string]> = [
    ['accountPrefix', filters.accountPrefix.trim()],
    ['periodId', filters.periodId],
    ['from', filters.from],
    ['to', filters.to],
    ['documentType', filters.documentType],
    ['side', filters.side],
  ];
  for (const [key, value] of singles) {
    if (value !== '') {
      params.set(key, value);
    }
  }
  for (const [slot, value] of Object.entries(filters.dims)) {
    if (value !== '') {
      params.set(slot, value);
    }
  }
  return params.toString();
}
